import React, { memo, useCallback, useEffect, useState } from 'react';

import { useIntl } from 'react-intl-next';
import { di } from 'react-magnetic-di';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import UFOLoadHold from '@atlaskit/react-ufo/load-hold';
import UFOSegment from '@atlaskit/react-ufo/segment';
import { ContextualAnalyticsData, fireUIAnalytics, MODAL } from '@atlassian/analytics-bridge';

import type { Surface } from './types';
import { cleanupRovoWidgetSDKScript, loadRovoWidgetSDKScript } from './utils';

const InjectRovoServiceAgentWidgetScript = memo(function RovoServiceAgentWidget({
	siteId,
	widgetId,
	surface,
}: {
	siteId: string;
	surface: Surface;
	widgetId: string;
}) {
	di(loadRovoWidgetSDKScript, fireUIAnalytics, useAnalyticsEvents, UFOLoadHold);

	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState<Error | null>(null);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { locale } = useIntl();

	const handleOnOpen = useCallback(() => {
		fireUIAnalytics(createAnalyticsEvent({}), 'rovoServiceAgentWidget opened');
	}, [createAnalyticsEvent]);

	const handleOnClose = useCallback(() => {
		fireUIAnalytics(createAnalyticsEvent({}), 'rovoServiceAgentWidget closed');
	}, [createAnalyticsEvent]);

	useEffect(() => {
		const loadScript = async () => {
			try {
				setIsLoading(true);
				setError(null);
				await loadRovoWidgetSDKScript({
					siteId,
					widgetId,
					surface,
					locale,
					onOpen: handleOnOpen,
					onClose: handleOnClose,
				});
			} catch (err) {
				setError(err instanceof Error ? err : new Error('Failed to load widget script'));
			}
		};

		loadScript().finally(() => {
			setIsLoading(false);
		});
	}, [widgetId, siteId, handleOnClose, handleOnOpen, surface, locale]);

	useEffect(() => {
		return () => {
			cleanupRovoWidgetSDKScript();
		};
	}, []);

	if (error) {
		throw error;
	}

	if (isLoading) {
		return <UFOLoadHold name="rovo-widget-loader" />;
	}

	return null;
});

export const RovoServiceAgentWidgetInjector = ({
	siteId,
	widgetId,
	surface,
}: {
	siteId: string;
	surface: Surface;
	widgetId: string;
}): React.JSX.Element => {
	di(UFOSegment, ContextualAnalyticsData);

	return (
		<UFOSegment name="rovo-service-agent-widget">
			<ContextualAnalyticsData
				sourceName="rovoServiceAgent"
				sourceType={MODAL}
				attributes={{ siteId, widgetId, surface }}
			>
				<InjectRovoServiceAgentWidgetScript siteId={siteId} widgetId={widgetId} surface={surface} />
			</ContextualAnalyticsData>
		</UFOSegment>
	);
};
